<template>
  <div class="o-component">
    <div class="o-container">
      <h2 class="a-h1 m-contact__title">{{ translate('contact.general.title') }}</h2>
      <div class="m-contact">
        <div class="m-contact__left">
          <div v-for="storeAddress of props.storeAddresses" class="m-contact__opening-hours">
            <h3 class="m-text__title a-h3">
              {{ storeAddress.name }}
            </h3>
            <div class="o-editor-content">
              {{ storeAddress.street }} {{ storeAddress.house_number }}{{ storeAddress.house_number_extension ? ' ' +
                storeAddress.house_number_extension : '' }}<br>
              {{ storeAddress.zipcode }} {{ storeAddress.city }}<br>
              <a :href="`mailto:${storeAddress.email}`" :title="storeAddress.email">{{ storeAddress.email }}</a><br>
              <a :href="`tel:${storeAddress.phone}`" :title="storeAddress.phone">{{ storeAddress.phone }}</a><br><br>
              <div v-html="storeAddress.note">
              </div>
              <section class="m-contact__opening-hours" v-if="storeAddress.opening_hours.length > 0">
                <strong>
                  {{ translate('contact.opening_hours.title') }}
                </strong>
                <table>
                  <tbody>
                    <tr v-for="openingHour of storeAddress.opening_hours">
                      <td>{{ openingHour.label }}</td>
                      <td>{{ openingHour.value }}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </div>
          </div>
        </div>
        <div class="m-contact__right" v-if="!contactFormAlreadySubmitted">
          <h3 class="m-text__title a-h3">
            {{ translate('contact.form.title') }}
          </h3>
          <form ref="form" class="m-contact__form" method="post" :action="props.formEndpoint" @submit="submitForm"
            autocomplete="on" novalidate :aria-busy="loading">

            <!-- Name -->
            <div>
              <div class="a-input__contact" :class="{ 'a-input--invalid': v$.name.$error }">
                <label class="a-input__contact" for="form_name">
                  {{ translate('contact.form.field_name_label') }}
                </label>
                <input class="a-input" id="form_name" type="text" name="form_name" v-model="formValues.name" maxlength="255"
                  :placeholder="translate('contact.form.field_name_label')" autocomplete="name"
                  @blur="v$.name.$touch()">
                <p v-if="v$.name.$errors.length > 0" class="a-input__label--error">
                  {{ v$.name.$errors[0].$message }}
                </p>
              </div>
            </div>
            <!-- Email -->
            <div>
              <div class="a-input__contact" :class="{ 'a-input--invalid': v$.email.$error }">
                <label class="a-input__contact" for="form_email">
                  {{ translate('contact.form.field_email_label') }}
                </label>
                <input class="a-input" id="form_email" type="email" name="form_email" v-model="formValues.email" maxlength="320"
                  :placeholder="translate('contact.form.field_email_label')" autocomplete="email"
                  @blur="v$.email.$touch()">
                <p v-if="v$.email.$errors.length > 0" class="a-input__label--error">
                  {{ v$.email.$errors[0].$message }}
                </p>
              </div>
            </div>
            <!-- Phone number -->
            <div>
              <div class="a-input__contact" :class="{ 'a-input--invalid': v$.phone.$error }">
                <label class="a-input__contact" for="form_phone">
                  {{ translate('contact.form.field_phone_label') }}
                </label>
                <input class="a-input" id="form_phone" type="tel" name="form_phone" maxlength="50"
                  :placeholder="translate('contact.form.field_phone_label')" v-model="formValues.phone"
                  autocomplete="tel" @blur="v$.phone.$touch()">
                <p v-if="v$.phone.$errors.length > 0" class="a-input__label--error">
                  {{ v$.phone.$errors[0].$message }}
                </p>
              </div>
            </div>
            <!-- Message -->
            <div>
              <div class="a-input__contact" :class="{ 'a-input--invalid': v$.message.$error }">
                <label class="a-input__contact" for="form_message">
                  {{ translate('contact.form.field_comment_label') }}
                </label>
                <textarea class="a-textarea" id="form_message" name="form_message" cols="30" rows="10" maxlength="2000"
                  v-model="formValues.message" :placeholder="translate('contact.form.field_comment_label')"
                  @blur="v$.message.$touch()">
                </textarea>
                <p v-if="v$.message.$errors.length > 0" class="a-input__label--error">
                  {{ v$.message.$errors[0].$message }}
                </p>
              </div>
            </div>
            <div class="m-contact__website" aria-hidden="true">
              <label for="form_website">Website</label>
              <input id="form_website" type="text" name="form_website" autocomplete="off" tabindex="-1">
            </div>
            <altcha-widget ref="captcha" challenge="/form/challenge" :language="language"
              display="invisible" auto="off" workers="2" configuration='{"humanInteractionSignature":false}'></altcha-widget>
            <p v-if="errorMessage" class="a-input__label--error" role="alert">{{ errorMessage }}</p>
            <button class="a-btn a-btn--primary" type="submit" :disabled="loading">
              {{ translate(loading ? 'contact.form.button_sending' : 'contact.form.button_submit') }}
            </button>
          </form>
        </div>
        <div class="u-col-start:6@md u-col-end:12@md" v-if="contactFormAlreadySubmitted">
          <h3 class="m-text__title a-h3">{{ translate('contact.form.title') }}</h3>
          <div class="o-editor-content">
            <p>
              {{ translate('contact.message.already_submitted') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { translate } from '../../helper-functions'
import { Ref, onMounted, ref } from 'vue'
import { useCookies } from 'vue3-cookies'
import useVuelidate from '@vuelidate/core'
import { email, maxLength, required } from '../../helper/validators'
import { StoreAddress } from '../../helper/address'
import 'altcha'
import 'altcha/i18n/nl'
import 'altcha/i18n/de'

interface Form {
  name: string
  email: string
  phone: string
  message: string
}

interface CaptchaWidget extends HTMLElement {
  verify(): Promise<{ payload: string } | null>
  reset(): void
}

const captcha = ref<CaptchaWidget | null>(null)
const language = document.documentElement.lang.split(/[-_]/)[0].toLowerCase() || 'en'
const loading: Ref<boolean> = ref(false)
const errorMessage: Ref<string> = ref('')
const form: Ref<HTMLFormElement | null> = ref(null)
const cookieApi = useCookies()
const contactFormCookieKey = 'contact-form-submitted'
const contactFormAlreadySubmitted: Ref<boolean> = ref(false)
const formValues: Ref<Form> = ref({
  name: '',
  email: '',
  phone: '',
  message: '',
})
const v$ = useVuelidate({
  name: {
    required,
    maxLength: maxLength(255)
  },
  email: {
    required,
    email,
    maxLength: maxLength(320)
  },
  phone: {
    maxLength: maxLength(50)
  },
  message: {
    required,
    maxLength: maxLength(2000)
  },
}, formValues)
const props = defineProps({
  storeAddresses: {
    type: Array<StoreAddress>,
    default: [],
    required: true,
  },
  formEndpoint: {
    type: String,
    default: '/form/contact',
    required: true,
  }
})

onMounted(() => {
  if (cookieApi.cookies.get(contactFormCookieKey) === '1') {
    contactFormAlreadySubmitted.value = true
  }
})

async function submitForm($event: Event) {
  $event.preventDefault()

  if (loading.value || contactFormAlreadySubmitted.value) {
    return
  }

  loading.value = true
  errorMessage.value = ''

  try {
    if (!await v$.value.$validate()) {
      return
    }

    const formElement = form.value
    const widget = captcha.value
    if (formElement === null || widget === null) {
      errorMessage.value = defaultErrorMessage()
      return
    }

    const verification = await widget.verify()
    if (!verification?.payload) {
      errorMessage.value = defaultErrorMessage()
      return
    }

    const serializedFormData = getSerializedFormData(formElement)
    serializedFormData.set('altcha', verification.payload)
    const endpoint = new URL(props.formEndpoint, window.location.origin)
    endpoint.searchParams.set('lang', language)

    const resp = await fetch(endpoint, {
      method: formElement.method,
      body: serializedFormData.toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })

    if (!resp.ok) {
      errorMessage.value = resp.status === 429 ? translate('contact.error.too_many_requests') : defaultErrorMessage()
      return
    }

    const expiresInMs = 1000 * 30

    cookieApi.cookies.set(contactFormCookieKey, '1', new Date(Date.now() + expiresInMs))
    contactFormAlreadySubmitted.value = true
  } catch (error: unknown) {
    errorMessage.value = defaultErrorMessage()
  } finally {
    captcha.value?.reset()
    loading.value = false
  }
}

function getSerializedFormData(formElement: HTMLFormElement): URLSearchParams {
  const formData = new FormData(formElement)
  let formDataRecord: Record<string, string> = {}
  formData.forEach((value: FormDataEntryValue, key: string) => {
    formDataRecord[key] = value.toString()
  })
  return new URLSearchParams(formDataRecord)
}

function defaultErrorMessage(): string {
  return translate('contact.error.general')
}
</script>
